import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { PlanbookEditTemplates } from "@shared/models/analytics/planbook"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { CardStack, CardStackAttributes } from "@shared/models/card-stack"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type EmberStore from "cc-frontend/services/ember-store"
import _ from "lodash"
import { cloneDeep, partial } from "lodash-es"
import type { PreparedAction } from "../../../action-doc-builder"
import type { Narrative } from "../../create-narrative"

export interface Payload {
  newId: string
  planbookId: string
  cardStackAttributes: CardStackAttributes
}

export function prepareSaveAsLessonTemplate(payload: Payload): PreparedAction {
  return {
    name: COURSE_DATE_SAVE_AS_TEMPLATE.name,
    payload: payload,
    patches: COURSE_DATE_SAVE_AS_TEMPLATE.patches(payload),
    undoPatches: COURSE_DATE_SAVE_AS_TEMPLATE.undoPatches,
    narrative: partial(COURSE_DATE_SAVE_AS_TEMPLATE.narrative, payload),
  }
}
export const COURSE_DATE_SAVE_AS_TEMPLATE = {
  name: "COURSE_DATE_SAVE_AS_TEMPLATE",

  patches(payload: Payload): Array<PatchModel> {
    let lessonAttributes = cloneDeep(payload.cardStackAttributes)
    lessonAttributes.version = 0
    lessonAttributes._revision = 0
    lessonAttributes.usedAs = "lesson-template"
    let lessonPatch = Patch.create("card-stack", payload.newId)
      .isCreatePatch()
      .set("relationships.planbook.data", {
        id: payload.planbookId,
        type: "planbook",
      })
      .set("meta", { permissions: ["can-edit-plan"] })

    _.forEach(lessonAttributes, (val, key) => {
      if (key === "cards") {
        // Ensure we don't set cards to explicit-null in Mongo (will prevent future $push ops).
        // I think that can happen if you fork a template that you lost read access to.
        lessonPatch.set(`attributes.${key}`, val ?? [])
      } else {
        lessonPatch.set(`attributes.${key}`, val)
      }
    })

    let planbookPatch = Patch.create("planbook", payload.planbookId).push(
      "relationships.cardStackTemplates.data",
      {
        id: payload.newId,
        type: "card-stack",
      },
    ).patch

    return [lessonPatch.patch, planbookPatch]
  },

  undoPatches: null,

  narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
    let models = {
      planbook: findFn("planbook", payload.planbookId),
    }
    let newEvent: PlanbookEditTemplates = {
      ...createBlankEvent(),
      root_id: payload.planbookId,
      root_type: "planbook",
      primary_object_type: "planbook",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),
      primary_object_id_and_ancestor_ids: [payload.planbookId],
      primary_verb: "edit",
      primary_object_specifier: "templates",
      adverbial_verb: "saving",
      adverbial_object_type: "template",
    }

    return {
      title: null,
      titleForAnalytics: "Lesson - Save to Library",
      description: null,
      context: {
        planbookTitle: models.planbook.attributes.title,
        planbookId: models.planbook.id,
      },
      activity: null,
      newAnalyticsEvents: [newEvent],
    }
  },
}

/**
 * Inserts the template created by COURSE_DATE_SAVE_AS_TEMPLATE into the local store,
 * so that it exists optimistically.
 */
export function insertNewTemplate(
  store: EmberStore,
  newId: string,
  planbookId: string,
  cardStackAttributes: CardStackAttributes,
): void {
  const attributes = cloneDeep(cardStackAttributes)
  attributes.version = 0
  attributes._revision = 0
  attributes.usedAs = "lesson-template"
  store.insertDocument({
    type: "card-stack",
    id: newId,
    attributes,
    relationships: {
      course: { data: null },
      planbook: {
        data: {
          id: planbookId,
          type: "planbook",
        },
      },
    },
    meta: { permissions: ["can-edit-plan"] },
  } satisfies CardStack)
}

export default COURSE_DATE_SAVE_AS_TEMPLATE
